// Captures made without a connection (PHASED-PLAN C7 "offline"). A write that cannot reach the server is kept in IndexedDB
// with the Idempotency-Key it was made with, and sent when the network returns: by the service worker through background
// sync (public/sw.js), or by flushQueue when the page is open. A replay with the same key cannot create a second record.
const DB_NAME = "offsilo-pwa";
const STORE = "captures";
const SYNC_TAG = "offsilo-captures";
const API = "/api/v1";

type Queued = { id?: number; method: "POST" | "PUT" | "PATCH" | "DELETE"; url: string; headers: Record<string, string>; body: string | null; at: string };

/** The queue database, created the same way the service worker creates it, so whichever opens first makes it. */
function openQueue(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/** Keeps one write for later and asks the browser to send the queue when the network is back. */
export async function queueRequest(method: Queued["method"], path: string, body?: unknown): Promise<void> {
  const item: Queued = {
    method,
    url: `${API}${path}`,
    headers: { "content-type": "application/json", "idempotency-key": crypto.randomUUID() },
    body: body === undefined ? null : JSON.stringify(body),
    at: new Date().toISOString(),
  };
  const db = await openQueue();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).add(item);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
  if ("serviceWorker" in navigator) {
    const registration = (await navigator.serviceWorker.ready) as ServiceWorkerRegistration & { sync?: { register(tag: string): Promise<void> } };
    await registration.sync?.register(SYNC_TAG).catch(() => undefined);
  }
}

/** Sends the queued writes now, in order. A server error or a lost connection stops the pass and keeps the rest. Returns how many went. */
export async function flushQueue(): Promise<number> {
  const db = await openQueue();
  const items = await new Promise<(Queued & { id: number })[]>((resolve, reject) => {
    const request = db.transaction(STORE, "readonly").objectStore(STORE).getAll();
    request.onsuccess = () => resolve(request.result as (Queued & { id: number })[]);
    request.onerror = () => reject(request.error);
  });
  let sent = 0;
  for (const item of items) {
    let status: number;
    try {
      status = (await fetch(item.url, { method: item.method, headers: item.headers, body: item.body ?? undefined, credentials: "same-origin" })).status;
    } catch {
      break;
    }
    if (status >= 500) break;
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(item.id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    sent += 1;
  }
  db.close();
  return sent;
}
